import {
  Component,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  TableConfig,
  TableColumn,
} from '../../interfaces/table-config.interface';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { logoBase64 } from '../../../../public/assets/logoBase64';
import { AuthService } from '../../services/auth.service';
import {
  DownloadDocumentsRequest,
  DownloadEndorsementRequest,
} from '../../interfaces/utilitarios.interface';

interface jsPDFWithAutoTable extends jsPDF {
  autoTable: (options: any) => jsPDFWithAutoTable;
}

interface AgingTotals {
  balance: number;
  noVencido: number;
  timeframe1: number;
  timeframe2: number;
  timeframe3: number;
  timeframe4: number;
  timeframe5: number;
  timeframe6: number;
  timeframe7: number;
  timeframe8: number;
  [key: string]: number;
}

@Component({
  selector: 'app-insurance-table',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './insurance-table.component.html',
  styleUrls: ['./insurance-table.component.css'],
})
export class InsuranceTableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: any[] = [];
  @Input() config!: TableConfig;
  @Input() configExport?: TableConfig;
  @Input() itemsPerPage: number = 6;
  @Input() mensajeNoData: string = 'No hay datos disponibles';
  @Input() error?: number;
  @Input() fechaCorte?: Date;
  /**
   * Recibe los totales calculados en el servidor, indexados por la key de la columna.
   * Si no se envía, la tabla calcula los totales con los datos actuales.
   */
  @Input() totals?: { [key: string]: number };
  @Input() isAging: boolean = false;
  @Input() showPagination: boolean = true;
  @Input () route: string = '';
  @Input() serverSidePagination: boolean = false;
  @Input() totalRecordsFromServer?: number;
  @Input() totalPagesFromServer?: number;

  @Output() actionClick = new EventEmitter<any>();
  @Output() loadOrders = new EventEmitter<any>();
  @Output() loadDocuments = new EventEmitter<DownloadDocumentsRequest>();
  @Output() printEndorsement = new EventEmitter<DownloadEndorsementRequest>();
  @Output() pageChange = new EventEmitter<number>();

  trackByFn(index: number, item: any): any {
    return (
      item.policyNumber || item.producerCode || item.documentNumber || index
    );
  }

  get mensajeMostrar(): string {
    if (this.error === 404) {
      const fecha = this.fechaCorte
        ? new Date(this.fechaCorte).toLocaleDateString()
        : '';
      return `No hay datos disponibles ${fecha}`;
    }
    return this.mensajeNoData;
  }
  user: string | null = '';
  currentPage: number = 1;
  totalPages: number = 0;
  totalRegistros: number = 0;
  calculatedTotals: AgingTotals = {
    balance: 0,
    noVencido: 0,
    timeframe1: 0,
    timeframe2: 0,
    timeframe3: 0,
    timeframe4: 0,
    timeframe5: 0,
    timeframe6: 0,
    timeframe7: 0,
    timeframe8: 0,
  };
  paginatedData: any[] = [];
  inputPage: number = 1;

  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' | '' = '';

  visibleRows: number = 0;
  visibleColumns: TableColumn[] = [];
  hiddenColumns: TableColumn[] = [];
  showColumnMenu: boolean = false;

  constructor(private authService: AuthService) {
    this.data = [];
  }

  ngOnInit() {
    this.calculateTotals();
    this.updatePaginatedData();
    this.adjustVisibleRows();
    this.adjustVisibleColumns();
    window.addEventListener('resize', this.adjustVisibleRows.bind(this));
    window.addEventListener('resize', this.adjustVisibleColumns.bind(this));
    this.user = this.authService.getProducerName();
  }

  ngOnChanges() {
    this.calculateTotals();
    this.updatePaginatedData();
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.adjustVisibleRows.bind(this));
    window.removeEventListener('resize', this.adjustVisibleColumns.bind(this));
  }

  private calculateTotals() {
    if (!this.data) {
      this.data = [];
    }

    if (this.serverSidePagination) {
      this.totalRegistros = this.totalRecordsFromServer ?? this.data.length;
      this.totalPages =
        this.totalPagesFromServer ??
        Math.ceil(this.totalRegistros / this.itemsPerPage);
    } else {
      this.totalRegistros = this.data.length;
      this.totalPages = Math.ceil(this.totalRegistros / this.itemsPerPage);
    }

    if (this.isAging) {
      this.calculatedTotals = {
        balance: 0,
        noVencido: 0,
        timeframe1: 0,
        timeframe2: 0,
        timeframe3: 0,
        timeframe4: 0,
        timeframe5: 0,
        timeframe6: 0,
        timeframe7: 0,
        timeframe8: 0,
      };

      this.paginatedData.forEach((item) => {
        Object.keys(this.calculatedTotals).forEach((key) => {
          const typedKey = key as keyof AgingTotals;
          this.calculatedTotals[typedKey] += Number(item[typedKey]) || 0;
        });
      });
    } else if (this.config.totalFields) {
      this.config.totalFields.forEach((field) => {
        this.calculatedTotals[field as keyof AgingTotals] = this.data.reduce(
          (sum, item) => sum + (Number(item[field]) || 0),
          0
        );
      });
    }
  }

  public updatePaginatedData(): void {
    if (this.serverSidePagination) {
      this.paginatedData = this.data;
      return;
    }
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedData = this.data.slice(startIndex, endIndex);
  }

  onSort(column: string): void {
    if (!this.config.columns.find((col) => col.key === column)?.sortable) {
      return;
    }

    if (this.sortColumn === column) {
      this.sortDirection =
        this.sortDirection === 'asc'
          ? 'desc'
          : this.sortDirection === 'desc'
          ? ''
          : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    if (this.sortDirection) {
      this.data = [...this.data].sort((a, b) => {
        const direction = this.sortDirection === 'asc' ? 1 : -1;
        return direction * this.compare(a[column], b[column]);
      });
      this.updatePaginatedData();
    }
  }

  private compare(a: any, b: any): number {
    if (typeof a === 'string') {
      return a.localeCompare(b);
    }
    return a - b;
  }

  formatValue(value: any, column: TableColumn): string {
    if (value === undefined || value === null) return '';

    switch (column.type) {
      case 'number':
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value);
      case 'percentage':
        return `${value}`;
      case 'currency':
        return value ? new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(value) : '$0';
      case 'date':
        if (!value) return '';

        try {
          // Limpiar la cadena de espacios extras al inicio y final
          const fechaLimpia = value.toString().trim();

          // Si tiene formato ISO con tiempo (2024-12-31T00:00:00)
          if (fechaLimpia.includes('T')) {
            const [fechaParte] = fechaLimpia.split('T');
            const [año, mes, dia] = fechaParte.split('-');
            return `${dia}/${mes}/${año}`;
          }

          // Si tiene formato con espacio y hora (11/10/2019 00:00:00)
          if (fechaLimpia.includes(' ')) {
            const [fechaParte] = fechaLimpia.split(' ');
            const partes = fechaParte.split('/');
            if (partes.length === 3) {
              const [dia, mes, año] = partes;
              return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${año}`;
            }
          }

          // Detectar el separador usado
          let separador = '';
          if (fechaLimpia.includes('-')) separador = '-';
          else if (fechaLimpia.includes('/')) separador = '/';
          else if (fechaLimpia.includes('.')) separador = '.';

          // Si no hay separador válido, intentar crear fecha
          if (!separador) {
            const fecha = new Date(fechaLimpia);
            if (!isNaN(fecha.getTime())) {
              return fecha.toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              });
            }
            return fechaLimpia;
          }

          // Separar los componentes de la fecha
          const partes = fechaLimpia.split(separador);
          if (partes.length !== 3) return fechaLimpia;

          // Determinar el formato basado en los componentes
          let dia, mes, año;

          // Si el primer componente tiene 4 dígitos, es año
          if (partes[0].length === 4) {
            [año, mes, dia] = partes;
          } else {
            // Asumimos formato dd/mm/yyyy
            [dia, mes, año] = partes;
          }

          // Validar componentes
          dia = dia.padStart(2, '0');
          mes = mes.padStart(2, '0');

          // Validar que sea una fecha válida
          const fecha = new Date(`${año}-${mes}-${dia}`);
          if (isNaN(fecha.getTime())) {
            return 'Fecha inválida';
          }

          return `${dia}/${mes}/${año}`;
        } catch (error) {
          return 'Fecha inválida';
        }

      case 'poliza':
        if (!value) return '';

        const str = value.toString().trim();

        // Verificar si ya tiene el formato x-x-xxx-xxxxxx
        if (/^\d-\d-\d{3}-\d+$/.test(str)) return str;

        // Validar que solo contenga números
        if (!/^\d+$/.test(str)) return str;

        // Validar longitud mínima
        if (str.length < 10) return str;

        // Aplicar formato x-x-xxx-xxxxxx
        try {
          return `${str.slice(0, 1)}-${str.slice(1, 2)}-${str.slice(
            2,
            5
          )}-${str.slice(5)}`;
        } catch {
          return str;
        }
      case 'year':
        if (!value) return '';
        return value.toString();
      default:
        return value.toString();
    }
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.inputPage = page;
      if (this.serverSidePagination) {
        this.pageChange.emit(page);
        return;
      }
      this.updatePaginatedData();
    }
  }

  getPaginationArray(): number[] {
    const pages = this.totalPages > 3 ? 3 : this.totalPages;
    return Array.from({ length: pages }, (_, i) => i + 1);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.inputPage = page;
      if (this.serverSidePagination) {
        this.pageChange.emit(page);
        return;
      }
      this.updatePaginatedData();
    }
  }

  getColumnLabel(field: string): string {
    const column = this.config.columns.find((col) => col.key === field);
    return column ? column.label : field;
  }

  /**
   * Formatea el total de una columna para mostrarlo en el pie de la tabla.
   * @param field Key de la columna a totalizar.
   * @returns Valor formateado según el tipo de la columna.
   */
  formatTotal(field: string): string {
    const column = this.config.columns.find((col) => col.key === field);
    if (!column) return '';

    return this.formatValue(this.resolveTotalValue(field), column);
  }

  /**
   * Resuelve el valor del total de un campo.
   * Usa el input `totals` cuando el campo existe ahí; si no, usa el cálculo local.
   * @param field Key de la columna a totalizar.
   * @returns Monto del total a mostrar.
   */
  private resolveTotalValue(field: string): number | undefined {
    if (this.isAging) {
      return this.totals?.[field];
    }

    if (this.totals && this.totals[field] !== undefined && this.totals[field] !== null) {
      return this.totals[field];
    }

    return this.calculatedTotals[field as keyof AgingTotals];
  }

  //Exportar datos
  exportarDatos(
    formato: string,
    headerInfo?: {
      titulo: string;
      fecha1: Date;
      fecha2?: Date;
      lob: string;
      slob: string;
      tableConfig: TableConfig;
    }
  ) {
    switch (formato) {
      case 'pdf':
        this.exportarPDF(headerInfo);
        break;
      case 'excel':
        this.exportarExcel(headerInfo);
        break;
      case 'text':
        this.exportarTexto(headerInfo);
        break;
      case 'word':
        this.exportarWord(headerInfo);
        break;
    }
  }

  dateByPage(page: string, fecha1: Date, fecha2: Date = new Date()): string {
    let date: string = '';

    let fecha1Format = new Date(
      new Date(fecha1).setDate(new Date(fecha1).getDate() + 1)
    ).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    let fecha2Format = new Date(
      new Date(fecha2).setDate(new Date(fecha2).getDate() + 1)
    ).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const fechaCorte = [
      'Mi Cartera',
      'Estado de Cuenta',
      'Antigüedad',
      'Sinestralidad',
    ];
    const fechaLapso = [
      'Mi Producción',
      'Cancelaciones',
      'Recibos de cobros',
      'Pólizas a Renovar',
      'Mis Reclamos',
      'Cotizaciones',
      'Descargas',
    ];

    if (fechaCorte.includes(page)) date = `Fecha corte: ${fecha1Format}`;
    else if (fechaLapso.includes(page))
      date = `Desde: ${fecha1Format}    Hasta: ${fecha2Format}`;

    return date;
  }

  private exportarExcel(headerInfo?: {
    titulo: string;
    fecha1: Date;
    fecha2?: Date;
    lob: string;
    slob: string;
    tableConfig: TableConfig;
  }) {

    /* En la descarga de csv de comisiones, las columnas monetarias deben ser de tipo number */
    if (this.route === '/comisiones') {
      const columns = headerInfo!.tableConfig.columns;
      const columnasNumericas = [
        'ammount',
        'grossPremium',
        'netPremium',
        'commission',
        'comision',
        'coverageValue',
      ];
      columns
        .filter((col) => columnasNumericas.includes(col.key))
        .forEach((col) => (col.type = 'number'));
    }
    
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([]);

    let rowIndex = 0;
    if (headerInfo) {
      // Título
      XLSX.utils.sheet_add_aoa(
        ws,
        [[headerInfo.titulo + ' - Portal Intermediario']],
        {
          origin: `A${++rowIndex}`,
        }
      );
      // Usuario
      XLSX.utils.sheet_add_aoa(ws, [[`${this.user}`]], {
        origin: `A${++rowIndex}`,
      });
      // Fecha
      const fecha = this.dateByPage(
        headerInfo!.titulo,
        headerInfo!.fecha1,
        headerInfo!.fecha2
      );
      XLSX.utils.sheet_add_aoa(ws, [[`${fecha}`]], {
        origin: `A${++rowIndex}`,
      });
      // Ramo
      XLSX.utils.sheet_add_aoa(
        ws,
        [[`Ramo: ${headerInfo.lob ? headerInfo.lob : 'Todos'}`]],
        {
          origin: `A${++rowIndex}`,
        }
      );
      // Subramo
      XLSX.utils.sheet_add_aoa(
        ws,
        [[`Sub-Ramo: ${headerInfo.slob ? headerInfo.slob : 'Todos'}`]],
        {
          origin: `A${++rowIndex}`,
        }
      );
      // Línea en blanco después de ramo/subramo
      rowIndex++; // Agrega una línea en blanco
    }

    const config = this.configExport || this.config;
    // En comisiones las columnas number se exportan como número crudo para que Excel las trate como numéricas
    const esComisiones = this.route === '/comisiones';
    // Agregar headers
    const headers = config.columns.map((col) => col.label);
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: `A${++rowIndex}` });

    // Agregar datos
    const datos = this.data.map((row) =>
      config.columns.map((col) => {
        const valor = row[col.key];

        if (
          esComisiones &&
          col.type === 'number' &&
          valor !== null &&
          valor !== undefined &&
          valor !== ''
        ) {
          return Number(valor);
        }

        return this.formatValue(valor, col);
      })
    );
    XLSX.utils.sheet_add_aoa(ws, datos, { origin: `A${++rowIndex}` });

    // Agregar totales si existen
    if (config.totalFields && config.totalFields.length > 0) {
      const totalRow = this.config.columns.map((col) => {
        if (this.config.totalFields?.includes(col.key)) {
          const total = this.totals?.[col.key] || 0;

          if (esComisiones && col.type === 'number') {
            return Number(total);
          }

          return this.formatValue(total, col);
        }
        return '';
      });
      XLSX.utils.sheet_add_aoa(ws, [totalRow], {
        origin: `A${rowIndex + datos.length + 1}`,
      });
    }

    // Aplicar estilos
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    const styleHeader = {
      fill: { fgColor: { rgb: '2980B9' } }, // Azul similar al de la tabla
      font: { color: { rgb: 'FFFFFF' }, bold: true },
      alignment: { horizontal: 'center' },
    };

    const styleCells = {
      alignment: { horizontal: 'left' },
      border: {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      },
    };

    // Aplicar estilos a las celdas
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellRef]) ws[cellRef] = { v: '', t: 's' };

        if (R === (headerInfo ? 3 : 0)) {
          // Fila de headers
          ws[cellRef].s = styleHeader;
        } else {
          ws[cellRef].s = styleCells;

          // En comisiones aplica formato numérico a las celdas de columnas number
          const columna = config.columns[C];
          if (
            esComisiones &&
            columna?.type === 'number' &&
            ws[cellRef].t === 'n'
          ) {
            ws[cellRef].z = '#,##0.00';
          }
        }
      }
    }

    // Ajustar ancho de columnas
    const wscols = this.config.columns.map(() => ({ wch: 20 }));
    ws['!cols'] = wscols;

    XLSX.utils.book_append_sheet(wb, ws, 'Datos');
    XLSX.writeFile(wb, `${headerInfo?.titulo}.xlsx`);
  }

  // Exportar a PDF
  private exportarPDF(headerInfo?: {
    titulo: string;
    fecha1: Date;
    fecha2?: Date;
    lob: string;
    slob: string;
    tableConfig: TableConfig;
  }) {
    const config = this.configExport || this.config;
    const doc = new jsPDF({ orientation: 'landscape' }) as any; // Orientación landscape
    const columns = config.columns.map((c) => c.label);
    let startY: number = 41;
    const notLobAndSlob = ['Cancelaciones'];

    // Header de la página
    const pageHeader = (doc: any) => {
      doc.addImage(logoBase64, 'PNG', 10, 9, 40, 13);
      doc.setFontSize(16);
      doc.text(`${headerInfo?.titulo} - Plataforma Intermediario`, 55, 15);
      doc.setFontSize(12);
      doc.text(`${this.user}`, 55, 20);

      doc.setFontSize(11);
      doc.text(
        this.dateByPage(
          headerInfo!.titulo,
          headerInfo!.fecha1,
          headerInfo!.fecha2
        ),
        14,
        28
      );

      if (!notLobAndSlob.includes(headerInfo!.titulo)) {
        doc.setFontSize(11);
        doc.text(
          `Ramo: ${headerInfo?.lob ? headerInfo?.lob : 'Todos'}`,
          14,
          33
        );
        doc.text(
          `Subramo: ${headerInfo?.slob ? headerInfo?.slob : 'Todos'}`,
          14,
          38
        );
      }
    };

    const body = this.data.map((item) =>
      config.columns.map((col) => this.formatearValor(item[col.key], col.type))
    );

    body.unshift(columns!);
    // La tabal de cancelaciones posse muchas columnas, siendo necesario un fontSize más pequeño
    const tableFontSize = headerInfo!.titulo === 'Cancelaciones' ? 7 : 10;
    if (headerInfo) {
      // Agregamos más espacio después del encabezado
      doc.autoTable({
        startY,
        body,
        margin: { left: 10, top: 43 },
        headStyles: {
          fillColor: [0, 85, 140], // Background color in RGB
          textColor: [255, 255, 255], // Text color (white)
          fontSize: 10,
        },
        columnStyles: {
          0: { halign: 'left' }, // Column 0 (Deudor) aligned to the left
          1: { halign: 'right' }, // Column 1 (Poliza) aligned to the center
          2: { halign: 'left' }, // Column 2 (Factura) aligned to the right
          3: { halign: 'right' }, // Column 2 (Documento) aligned to the right
          4: { halign: 'right' }, // Column 2 (Emision) aligned to the right
          5: {
            halign: headerInfo.titulo === 'Mis Reclamos' ? 'left' : 'right',
          },
          6: { halign: 'right' }, // Column 2 (Prima Neta) aligned to the right
          7: { halign: 'right' }, // Column 2 (Participacion) aligned to the right
          8: { halign: 'right' }, // Column 2 (% comision) aligned to the right
          9: { halign: 'right' }, // Column 2 (Monto comision) aligned to the right
          10: { halign: 'right' }, // Column 2 (Monto comision) aligned to the right
        },
        styles: {
          fontSize: tableFontSize, // Set the font size for the entire table
        },
        didDrawCell: (data: any) => {
          const { cell } = data;
          const x = cell.x;
          const y = cell.y;
          const w = cell.width;
          const h = cell.height;
        },
        didParseCell: function (row: any) {
          // Aplica estilos solo a la primera fila del body
          if (row.row.index === 0 && row.section === 'body') {
            // row.cell.styles.fillColor = [0, 85, 140]; // Color de fondo azul
            row.cell.styles.valign = 'bottom'; // Grosor del borde
            row.cell.styles.lineWidth = 0; // Grosor del borde
            row.cell.styles.lineWidth = { bottom: 1 }; // Grosor del borde
            row.cell.styles.lineColor = [255, 165, 0]; // Borde naranja (RGB)
            row.cell.styles.fillColor = [255, 255, 255]; // Fondo blanco opcional
            row.cell.styles.textColor = [0, 0, 0]; // Texto blanco
            row.cell.styles.fontStyle = 'bold'; // Texto en negrita
            row.cell.styles.fontSize = tableFontSize; // Texto en negrita
          }
        },
        didDrawPage: () => pageHeader(doc),
      });
      // Actualiza la posición en Y para el siguiente "Deudor" de la página
    }

    doc.save(`${headerInfo!.titulo}.pdf`);
  }

  // Exportar a Texto
  private exportarTexto(headerInfo?: {
    titulo: string;
    fecha1: Date;
    fecha2?: Date;
    lob: string;
    slob: string;
    tableConfig: TableConfig;
  }) {
    let content = '';
    const config = this.configExport || this.config;
    // Construir contenido CSV (sin encabezado adicional)
    const rows = this.data
      .map(
        (row) =>
          config.columns
            .map((col) => this.formatValue(row[col.key], col).trim())
            .join('|') // Usar | como separador
      )
      .join('\n');

    content += rows;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${headerInfo?.titulo || 'archivo'}.txt`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  // Exportar a Word
  private exportarWord(headerInfo?: {
    titulo: string;
    fecha1: Date;
    fecha2?: Date;
    lob: string;
    slob: string;
    tableConfig: TableConfig;
  }) {
    const config = this.configExport || this.config;
    let content = `formatValue
     <html>
        <head>
          <style>
            @page { size: A4 landscape; margin: 20mm; }
            body { font-family: Arial, sans-serif; margin: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 12px}
            th { background-color: #2980B9; color: white; padding: 8px; text-align: left; border: 1px solid #ddd; }
            td { padding: 8px; border: 1px solid #ddd; }
            .header { display: flex; margin-bottom: 20px; }
            .title { font-size: 20px; font-weight: bold; }
            .subtitle { font-size: 18px; }
            .date { font-size: 14px; margin-top: 5px; }
            .user { font-weigth: bold; margin-top: 5px; }
            .spacer { margin-top: 15px; }
            .debtor-section { margin-top: 40px; page-break-after: always; }
            .debtor-section:last-child { page-break-after: auto; }
            .image { width: 50px}
          </style>
        </head>
        <body>
    `;

    // Agregar título y fecha si existen
    const fecha = this.dateByPage(
      headerInfo!.titulo,
      headerInfo!.fecha1,
      headerInfo!.fecha2
    );
    content += `
    <div class="header">
            <div>
                <img class='image' src="${logoBase64}" />
            </div>
            <div>
                <div class="title">${
                  headerInfo?.titulo
                } - Portal Intermediario</div>
                <div class="user">${this.user}</div>
                <div class="date">${fecha}</div>
                <div>${`Ramo: ${
                  headerInfo?.lob ? headerInfo?.lob : 'Todos'
                }`}</div>
                <div>${`Subramo: ${
                  headerInfo?.slob ? headerInfo?.slob : 'Todos'
                }`}</div>
            </div>
            <div class="spacer"></div> <!-- Agregamos div con espacio -->
        </div>
  `;

    // Crear tabla
    content += '<table><thead><tr>';

    // Headers
    config.columns.forEach((col) => {
      content += `<th>${col.label}</th>`;
    });
    content += '</tr></thead><tbody>';

    // Datos
    this.data.forEach((row) => {
      content += '<tr>';
      config.columns.forEach((col) => {
        content += `<td>${this.formatValue(row[col.key], col)}</td>`;
      });
      content += '</tr>';
    });

    // Totales si existen
    if (config.totalFields && config.totalFields.length > 0) {
      content += '<tr>';
      config.columns.forEach((col) => {
        if (config.totalFields?.includes(col.key)) {
          content += `<td>${this.formatValue(
            this.totals?.[col.key] || 0,
            col
          )}</td>`;
        } else {
          content += '<td></td>';
        }
      });
      content += '</tr>';
    }

    content += '</tbody></table></body></html>';

    const blob = new Blob([content], {
      type: 'application/msword',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${headerInfo?.titulo}.doc`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  adjustVisibleRows() {
    const width = window.innerWidth;
    if (width < 600) {
      this.visibleRows = 2;
    } else if (width < 900) {
      this.visibleRows = 3;
    } else {
      this.visibleRows = 5; // Ajusta según sea necesario
    }
  }

  toggleRows(): void {
    this.visibleRows =
      this.visibleRows === this.paginatedData.length
        ? 0
        : this.paginatedData.length;
  }

  adjustVisibleColumns() {
    const width = window.innerWidth;
    if (width < 600) {
      this.visibleColumns = this.config.columns.slice(0, 2);
    } else if (width < 900) {
      this.visibleColumns = this.config.columns.slice(0, 3);
    } else {
      this.visibleColumns = [...this.config.columns];
    }
    this.hiddenColumns = this.config.columns.slice(this.visibleColumns.length);
  }

  toggleColumns(): void {
    if (this.visibleColumns.length === this.config.columns.length) {
      // Si están todas las columnas visibles, mostrar solo las primeras 2
      this.visibleColumns = this.config.columns.slice(0, 2);
    } else {
      // Si no están todas visibles, mostrar todas las columnas
      this.visibleColumns = [...this.config.columns];
    }
    // Actualizar las columnas ocultas
    this.hiddenColumns = this.config.columns.filter(
      (col) => !this.visibleColumns.includes(col)
    );
  }

  toggleColumnMenu() {
    this.showColumnMenu = !this.showColumnMenu;
  }

  switchColumn(column: TableColumn) {
    this.visibleColumns[this.visibleColumns.length - 1] = column;
    this.hiddenColumns = this.config.columns.filter(
      (col) => !this.visibleColumns.includes(col)
    );
    this.showColumnMenu = false;
  }

  private formatearValor(valor: any, tipo?: string): string {
    if ((valor ?? '') === '') return '';

    switch (tipo) {
      case 'currency':
        return  valor ? new Intl.NumberFormat('en-Us', {
          style: 'currency',
          currency: 'USD',
        }).format(valor) : '$0';
      case 'percentage':
        return `${valor}`;
      case 'poliza':
        if (!valor) return '';

        const str = valor.toString().trim();

        // Verificar si ya tiene el formato x-x-xxx-xxxxxx
        if (/^\d-\d-\d{3}-\d+$/.test(str)) return str;

        // Validar que solo contenga números
        if (!/^\d+$/.test(str)) return str;

        // Validar longitud mínima
        if (str.length < 10) return str;

        // Aplicar formato x-x-xxx-xxxxxx
        try {
          return `${str.slice(0, 1)}-${str.slice(1, 2)}-${str.slice(
            2,
            5
          )}-${str.slice(5)}`;
        } catch {
          return str;
        }

      case 'date':
        if (!valor) return '';

        try {
          // Limpiar la cadena de espacios extras al inicio y final
          const fechaLimpia = valor.toString().trim();

          // Si tiene formato ISO con tiempo (2024-12-31T00:00:00)
          if (fechaLimpia.includes('T')) {
            const [fechaParte] = fechaLimpia.split('T');
            const [año, mes, dia] = fechaParte.split('-');
            return `${dia}/${mes}/${año}`;
          }

          // Si tiene formato con espacio y hora (11/10/2019 00:00:00)
          if (fechaLimpia.includes(' ')) {
            const [fechaParte] = fechaLimpia.split(' ');
            const partes = fechaParte.split('/');
            if (partes.length === 3) {
              const [dia, mes, año] = partes;
              return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${año}`;
            }
          }

          // Detectar el separador usado
          let separador = '';
          if (fechaLimpia.includes('-')) separador = '-';
          else if (fechaLimpia.includes('/')) separador = '/';
          else if (fechaLimpia.includes('.')) separador = '.';

          // Si no hay separador válido, intentar crear fecha
          if (!separador) {
            const fecha = new Date(fechaLimpia);
            if (!isNaN(fecha.getTime())) {
              return fecha.toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              });
            }
            return fechaLimpia;
          }

          // Separar los componentes de la fecha
          const partes = fechaLimpia.split(separador);
          if (partes.length !== 3) return fechaLimpia;

          // Determinar el formato basado en los componentes
          let dia, mes, año;

          // Si el primer componente tiene 4 dígitos, es año
          if (partes[0].length === 4) {
            [año, mes, dia] = partes;
          } else {
            // Asumimos formato dd/mm/yyyy
            [dia, mes, año] = partes;
          }

          // Validar componentes
          dia = dia.padStart(2, '0');
          mes = mes.padStart(2, '0');

          // Validar que sea una fecha válida
          const fecha = new Date(`${año}-${mes}-${dia}`);
          if (isNaN(fecha.getTime())) {
            return 'Fecha inválida';
          }

          return `${dia}/${mes}/${año}`;
        } catch (error) {
          return 'Fecha inválida';
        }

      default:
        return valor.toString();
    }
  }

  onActionClick(item: any) {
    this.actionClick.emit(item);
  }

  onSelectClick(item: any) {
    this.loadOrders.emit(item);
  }

  // Cargar documentos
  onLoadDocuments(item: any) {
    const request: DownloadDocumentsRequest = {
      process: item.process,
      Policy: item.policyNumber?.replaceAll('-', ''),
      DebtorCode: item.debtorCode,
      DocumentNumber: item.documentNumber,
      EffectiveDate: new Date(item.effectiveDate),
      DocumentType: Number(item.documentType),
      cuser: this.authService.getUserId(),
    };

    this.loadDocuments.emit(request);
  }
}
